import { paragraphsOf, type Paragraph } from "./paragraphs";

/**
 * The studio's one edit mode edits a chapter paragraph by paragraph
 * (ADR-0051). This is the bookkeeping behind it, kept pure so the rules that
 * decide what gets saved — and where a picture goes afterwards — are tested
 * without a browser.
 *
 * A field is the text box the author types into. It stands for a run of the
 * saved body's paragraphs, `[from, to)`: one, normally; several only while a
 * field that was split by a typed line break still has the caret; none for a
 * field whose paragraph has not been saved yet.
 *
 * Saving never rebuilds the body from the fields. It replaces the edited
 * fields' ranges inside the saved body and leaves every other character where
 * it was, spacing included, so narration sees no change it did not get.
 */
export interface Field {
  /** Stable across saves, so React keeps the box — and the caret — in place. */
  key: string;
  from: number;
  to: number;
  text: string;
}

export type KeyMaker = () => string;

/** fieldsOf is a field per paragraph of a saved body, and one to type into when it has none. */
export function fieldsOf(body: string, key: KeyMaker): Field[] {
  const paragraphs = paragraphsOf(body);
  if (paragraphs.length === 0) return [{ key: key(), from: 0, to: 0, text: "" }];
  return paragraphs.map((p) => ({ key: key(), from: p.index, to: p.index + 1, text: p.text }));
}

/** rangeOf is where a field's paragraphs sit in the saved body. */
function rangeOf(body: string, paragraphs: Paragraph[], field: Field) {
  if (field.to > field.from) {
    return { start: paragraphs[field.from]!.start, end: paragraphs[field.to - 1]!.end };
  }
  const at = field.from < paragraphs.length ? paragraphs[field.from]!.start : body.length;
  return { start: at, end: at };
}

/** savedText is what a field held when the body was saved. */
export function savedText(body: string, paragraphs: Paragraph[], field: Field): string {
  const { start, end } = rangeOf(body, paragraphs, field);
  return body.slice(start, end);
}

export function isDirty(body: string, paragraphs: Paragraph[], field: Field): boolean {
  return field.text !== savedText(body, paragraphs, field);
}

/**
 * applyFields is the saved body with every edited field written into its own
 * range, and nothing else touched.
 */
export function applyFields(body: string, fields: Field[]): string {
  const paragraphs = paragraphsOf(body);
  const edits = fields
    .filter((f) => isDirty(body, paragraphs, f))
    .map((f) => {
      const { start, end } = rangeOf(body, paragraphs, f);
      let text = f.text;
      // A field with no paragraph of its own is new text: it needs its own
      // line, or it runs into the paragraph beside it.
      if (start === end && text !== "") {
        if (f.from < paragraphs.length) text = `${text}\n`;
        else if (body !== "" && !body.endsWith("\n")) text = `\n${text}`;
      }
      return { start, end, text };
    })
    .sort((a, b) => b.start - a.start);
  let out = body;
  for (const e of edits) out = out.slice(0, e.start) + e.text + out.slice(e.end);
  return out;
}

/**
 * A save in flight: what every field said when it was sent, and which of them
 * were edited.
 */
export interface Snapshot {
  texts: Map<string, string>;
  edited: Set<string>;
}

export function snapshotOf(body: string, fields: Field[]): Snapshot {
  const paragraphs = paragraphsOf(body);
  return {
    texts: new Map(fields.map((f) => [f.key, f.text])),
    edited: new Set(fields.filter((f) => isDirty(body, paragraphs, f)).map((f) => f.key)),
  };
}

/**
 * afterSave is the fields for the newly saved body, and where each picture
 * under an edited field goes.
 *
 * Every field becomes one field per paragraph it now produces. The exceptions
 * keep their box and their words as they are: the one with the caret, and any
 * typed into while the save was on its way — replacing either would move text
 * out from under the author. A field that split keeps its key on its *last*
 * paragraph, because "after this field" means after the last of them.
 *
 * `follows` maps an old paragraph index under an edited field to the index its
 * picture should move to: the last paragraph that field produced, or null when
 * the field was emptied — nothing moves onto words nobody chose (ADR-0051).
 *
 * Returns null when the new body does not divide the way the snapshot says it
 * should, which would make every index below it a guess.
 */
export function afterSave(
  fields: Field[],
  snapshot: Snapshot,
  newBody: string,
  focused: string | null,
  key: KeyMaker,
): { fields: Field[]; follows: Map<number, number | null> } | null {
  const paragraphs = paragraphsOf(newBody);
  const out: Field[] = [];
  const follows = new Map<number, number | null>();
  let cursor = 0;
  for (const f of fields) {
    const sent = snapshot.texts.get(f.key);
    if (sent === undefined) return null;
    const edited = snapshot.edited.has(f.key);
    const count = edited ? paragraphsOf(sent).length : f.to - f.from;
    const from = cursor;
    cursor += count;
    if (cursor > paragraphs.length) return null;
    if (edited) {
      for (let p = f.from; p < f.to; p++) follows.set(p, count > 0 ? cursor - 1 : null);
    }

    if (f.key === focused || f.text !== sent) {
      out.push({ key: f.key, from, to: cursor, text: f.text });
      continue;
    }
    for (let i = from; i < cursor; i++) {
      out.push({
        key: i === cursor - 1 ? f.key : key(),
        from: i,
        to: i + 1,
        text: paragraphs[i]!.text,
      });
    }
  }
  if (cursor !== paragraphs.length) return null;
  if (out.length === 0) out.push({ key: key(), from: 0, to: 0, text: "" });
  return { fields: out, follows };
}

/**
 * pictureSlot is the paragraph a picture added after this field follows: its
 * last paragraph, or — for a field with none yet — the one before it.
 */
export function pictureSlot(field: Field): number {
  return field.to > field.from ? field.to - 1 : field.from - 1;
}

/**
 * restore lays edits kept on the device back over the fields of the body they
 * were made to, each over the paragraphs it stood for.
 */
export function restore(fields: Field[], edits: Omit<Field, "key">[], key: KeyMaker): Field[] {
  let out = fields;
  for (const e of [...edits].sort((a, b) => b.from - a.from)) {
    const at = out.findIndex((f) => f.from >= e.from);
    const index = at < 0 ? out.length : at;
    const covered = out.filter((f) => f.from >= e.from && f.to <= e.to && f.to > f.from).length;
    out = [
      ...out.slice(0, index),
      { key: key(), ...e },
      ...out.slice(index + (e.to > e.from ? covered : 0)),
    ];
  }
  return out;
}

/** locate finds a passage in the fields, preferring the one it was selected in. */
export function locate(
  fields: Field[],
  passage: string,
  prefer: { key: string; start: number },
): { key: string; start: number; end: number } | null {
  const own = fields.find((f) => f.key === prefer.key);
  if (own && own.text.slice(prefer.start, prefer.start + passage.length) === passage) {
    return { key: own.key, start: prefer.start, end: prefer.start + passage.length };
  }
  for (const f of fields) {
    const at = f.text.indexOf(passage);
    if (at >= 0) return { key: f.key, start: at, end: at + passage.length };
  }
  return null;
}
